/**
 * Definiciones de columnas para DataGrid de envíos
 */
import React from 'react';
import { Box} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningIcon from '@mui/icons-material/Warning';
import { GridActionsCellItem } from '@mui/x-data-grid';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import CommentIcon from '@mui/icons-material/Comment';
import Utils from '../../../utils/Utils';
import { estaEstadoBloqueado, generarUrlTracking } from './formato';
import { esClienteFinal } from './filtros';
import ChipClienteFinal from '../components/ui/ChipClienteFinal';

// Mapa de colores de estados (paleta semántica profesional)
const COLORES_ESTADO = {
    '-1': '#D1D5DB',  // Gris claro - Estado bloqueado
    '0': '#D1D5DB',   // Gris claro - Estado bloqueado
    '1': '#3B82F6',   // Azul - Expedición registrada
    '2': '#9CA3AF',   // Gris - Sin estado
    '3': '#F59E0B',   // Ámbar - Pendiente salida a reparto
    '4': '#06B6D4',   // Cyan - En reparto
    '5': '#22C55E',   // Verde - Entregado
    '6': '#B45309',   // Naranja oscuro - Retenido en almacén
    '7': '#8B5CF6',   // Púrpura - En tránsito entre islas
    '9': '#EF4444',   // Rojo - Cancelado
};

/**
 * Genera las columnas del DataGrid de envíos
 * @param {Function} onOpenConfirmEstado - Handler para abrir modal de confirmar estado
 * @param {Function} onOpenComment - Handler para abrir modal de comentario
 * @param {string} ejercicio - Año de ejercicio
 * @returns {Array} Columnas configuradas
 */
export const generarColumnas = (onOpenConfirmEstado, onOpenComment, ejercicio) => [
    {
        field: 'serie',
        headerName: 'Serie',
        flex: 0.5,
        editable: false,
    },
    {
        field: 'albaran',
        headerName: 'Número',
        flex: 0.5,
        editable: false,
    },
    {
        field: 'counterDelivery',
        headerName: 'Nº ruta',
        flex: 0.5,
        editable: false,
    },
    {
        field: 'nombre',
        headerName: 'Cliente',
        flex: 1.5,
        editable: false,
        renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.value}
                </Box>
                {esClienteFinal(params.row) && <ChipClienteFinal />}
            </Box>
        ),
    },
    {
        field: 'bultos',
        headerName: 'Bultos',
        flex: 0.5,
        editable: false,
        type: 'number',
    },
    {
        field: 'agencia',
        headerName: 'Agencia',
        flex: 1,
        editable: false,
    },
    {
        field: 'nameDelivery',
        headerName: 'Ruta',
        flex: 0.5,
        editable: false,
    },
    {
        field: 'tracking_destino',
        headerName: 'Isla',
        flex: 1,
        editable: false,
    },
    {
        field: 'ruta_fecha',
        headerName: 'F. ruta',
        flex: 0.75,
        editable: false,
    },
    {
        field: 'expedicionAsigna',
        headerName: 'Expedición',
        flex: 0.75,
        editable: false,
    },
    {
        field: 'zona',
        headerName: 'Zona',
        flex: 0.75,
        editable: false,
    },
    {
        field: 'tracking_descripcion',
        headerName: 'Estado',
        flex: 1.6,
        editable: false,
        renderCell: (params) => {
            const estado = params.row.tracking_estado;
            const texto = params.value
                ? Utils.toCapitalize(params.value)
                : 'Sin estado';

            // Obtener color del estado
            const colorEstado = COLORES_ESTADO[String(estado)] || '#6c757d';

            const contenido = (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 500,
                        color: colorEstado,
                    }}
                >
                    <LocalShippingIcon fontSize="small" />
                    {texto}
                </Box>
            );

            const url = generarUrlTracking(params.row, ejercicio);

            if (!url) {
                return contenido;
            }

            return (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: colorEstado }}
                >
                    {contenido}
                </a>
            );
        },
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        flex: 0.6,
        sortable: false,
        filterable: false,
        getActions: ({ id, row }) => {
            const estado = row?.tracking_estado;
            const tieneComentario = String(row?.tracking_comentario ?? '').trim() !== '';
            const estadoBloqueado = estaEstadoBloqueado(estado);

            const actions = [];

            if (!estadoBloqueado) {
                actions.push(
                    <GridActionsCellItem
                        key="forzar-estado"
                        icon={<PublishedWithChangesIcon />}
                        label="Forzar estado a entregado"
                        onClick={() => onOpenConfirmEstado(row)}
                        showInMenu={false}
                        sx={{ color: '#495057' }}
                    />
                );
            }

            if (tieneComentario || !estadoBloqueado) {
                actions.push(
                    <GridActionsCellItem
                        key="comentario"
                        icon={<CommentIcon />}
                        label="Añadir comentario"
                        onClick={() => onOpenComment(row)}
                        showInMenu={false}
                        sx={{ color: '#495057' }}
                    />
                );
            }

            return actions;
        },
    },
    {
        field: 'hasComment',
        headerName: '',
        width: 40,
        sortable: false,
        filterable: false,
        align: 'center',
        renderCell: (params) => {
            const comentario = String(params.row?.tracking_comentario ?? '').trim();
            if (!comentario) return null;

            return (
                <WarningIcon
                    fontSize="small"
                    sx={{ color: 'warning.main', opacity: 0.6 }}
                    titleAccess="Tiene comentario"
                />
            );
        },
    },
];
